// Create Auto-Pause Rule Modal with Template Gallery (Madgicx-Inspired)
import React, { useState, useEffect } from 'react';
import { autoPauseApi } from '../../lib/autoPauseApi';
import { campaignsApi } from '../../lib/campaignsApi';
import type { AutoPauseRuleCreate } from '../../types/auto-pause';
import type { Campaign } from '../../types/campaign';

// Rule Templates (Madgicx-Inspired)
export const RULE_TEMPLATES = [
  // Defensive (Stop Loss)
  {
    id: 'low-roas',
    name: '🛡️ Low ROAS Protection',
    description: 'Pause campaigns with ROAS below 2x for 2 days',
    category: 'defensive',
    icon: '🛡️',
    metric: 'roas' as const,
    operator: 'lt' as const,
    threshold: 2.0,
    duration_value: 2,
    duration_unit: 'days' as const,
  },
  {
    id: 'high-cpa',
    name: '💵 High CPA Alert',
    description: 'Pause if cost per acquisition exceeds $50 for 1 day',
    category: 'defensive',
    icon: '💵',
    metric: 'cpa' as const,
    operator: 'gt' as const,
    threshold: 50.0,
    duration_value: 1,
    duration_unit: 'days' as const,
  },
  {
    id: 'zero-conversions',
    name: '⚠️ Zero Conversions',
    description: 'Pause campaigns with no conversions for 3 days',
    category: 'defensive',
    icon: '⚠️',
    metric: 'conversions' as const,
    operator: 'eq' as const,
    threshold: 0,
    duration_value: 3,
    duration_unit: 'days' as const,
  },
  {
    id: 'negative-roi',
    name: '🚨 Negative ROI Stop',
    description: 'Pause immediately if ROAS falls below 1x',
    category: 'defensive',
    icon: '🚨',
    metric: 'roas' as const,
    operator: 'lt' as const,
    threshold: 1.0,
    duration_value: 1,
    duration_unit: 'days' as const,
  },
  // Budget Management
  {
    id: 'budget-overspend',
    name: '💰 Budget Overspend',
    description: 'Pause if daily spend exceeds 150% of target',
    category: 'budget',
    icon: '💰',
    metric: 'spend' as const,
    operator: 'gt' as const,
    threshold: 150.0, // User should customize this
    duration_value: 1,
    duration_unit: 'days' as const,
  },
  {
    id: 'underspend-alert',
    name: '📉 Underspend Alert',
    description: 'Pause if daily spend is below 50% of target for 2 days',
    category: 'budget',
    icon: '📉',
    metric: 'spend' as const,
    operator: 'lt' as const,
    threshold: 50.0, // User should customize this
    duration_value: 2,
    duration_unit: 'days' as const,
  },
  // Performance
  {
    id: 'low-ctr',
    name: '📊 Low CTR Protection',
    description: 'Pause if click-through rate drops below 0.5% for 3 days',
    category: 'performance',
    icon: '📊',
    metric: 'ctr' as const,
    operator: 'lt' as const,
    threshold: 0.5,
    duration_value: 3,
    duration_unit: 'days' as const,
  },
  {
    id: 'high-frequency',
    name: '🔁 High Frequency Cap',
    description: 'Pause if frequency exceeds 5 for 2 days (ad fatigue)',
    category: 'performance',
    icon: '🔁',
    metric: 'frequency' as const,
    operator: 'gt' as const,
    threshold: 5.0,
    duration_value: 2,
    duration_unit: 'days' as const,
  },
  // Advocacy-Specific
  {
    id: 'donation-protection',
    name: '💚 Donation Campaign Protection',
    description: 'Pause if conversion rate drops below 1% for 3 days',
    category: 'advocacy',
    icon: '💚',
    metric: 'cvr' as const,
    operator: 'lt' as const,
    threshold: 1.0,
    duration_value: 3,
    duration_unit: 'days' as const,
  },
  {
    id: 'petition-safety',
    name: '✍️ Petition Campaign Safety',
    description: 'Pause if cost per signature exceeds $2 for 2 days',
    category: 'advocacy',
    icon: '✍️',
    metric: 'cpa' as const,
    operator: 'gt' as const,
    threshold: 2.0,
    duration_value: 2,
    duration_unit: 'days' as const,
  },
];

interface CreateAutoPauseRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRuleCreated: () => void;
  campaignId?: string;
  editRule?: any; // For edit mode
}

export const CreateAutoPauseRuleModal: React.FC<CreateAutoPauseRuleModalProps> = ({
  isOpen,
  onClose,
  onRuleCreated,
  campaignId: initialCampaignId,
  editRule,
}) => {
  const [step, setStep] = useState<'templates' | 'form'>('templates');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // Form state
  const [campaignId, setCampaignId] = useState(initialCampaignId || '');
  const [metric, setMetric] = useState('roas');
  const [operator, setOperator] = useState('lt');
  const [threshold, setThreshold] = useState('');
  const [durationValue, setDurationValue] = useState('3');
  const [durationUnit, setDurationUnit] = useState('days');

  // Load campaigns when modal opens
  useEffect(() => {
    if (isOpen && !initialCampaignId) {
      loadCampaigns();
    }
  }, [isOpen, initialCampaignId]);

  const loadCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const data = await campaignsApi.list({ status: 'active' });
      setCampaigns(data);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      // Fall back to empty list, form will still work
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Load edit rule data
  useEffect(() => {
    if (editRule) {
      setCampaignId(editRule.campaign_id);
      setMetric(editRule.metric);
      setOperator(editRule.operator);
      setThreshold(editRule.threshold.toString());
      setDurationValue(editRule.duration_value.toString());
      setDurationUnit(editRule.duration_unit);
      setStep('form');
    }
  }, [editRule]);

  const handleTemplateSelect = (template: typeof RULE_TEMPLATES[0]) => {
    setMetric(template.metric);
    setOperator(template.operator);
    setThreshold(template.threshold.toString());
    setDurationValue(template.duration_value.toString());
    setDurationUnit(template.duration_unit);
    setStep('form');
  };

  const getPreviewText = () => {
    if (!threshold || !durationValue) return '';
    const metricName = metric.toUpperCase();
    const opSymbol = operator === 'lt' ? '<' : operator === 'gt' ? '>' : operator === 'lte' ? '≤' : operator === 'gte' ? '≥' : '=';
    return `IF ${metricName} ${opSymbol} ${threshold} FOR ${durationValue} ${durationUnit} THEN pause campaign`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const ruleData: AutoPauseRuleCreate = {
        campaign_id: campaignId,
        metric,
        operator: operator as 'lt' | 'gt' | 'eq' | 'lte' | 'gte',
        threshold: parseFloat(threshold),
        duration_value: parseInt(durationValue, 10),
        duration_unit: durationUnit as 'hours' | 'days',
      };

      if (editRule) {
        await autoPauseApi.update(editRule.id, ruleData);
      } else {
        await autoPauseApi.create(ruleData);
      }

      onRuleCreated();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create rule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('templates');
    setCampaignId(initialCampaignId || '');
    setMetric('roas');
    setOperator('lt');
    setThreshold('');
    setDurationValue('3');
    setDurationUnit('days');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-boxdark rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-black dark:text-white">
            {editRule ? 'Edit Auto-Pause Rule' : 'Create Auto-Pause Rule'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Template Gallery */}
        {step === 'templates' && !editRule && (
          <div>
            <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
              Quick Start Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {RULE_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="p-4 border-2 border-stroke dark:border-strokedark rounded-lg hover:border-primary dark:hover:border-primary transition text-left"
                >
                  <div className="text-3xl mb-2">{template.icon}</div>
                  <h4 className="font-semibold text-black dark:text-white mb-1">
                    {template.name}
                  </h4>
                  <p className="text-sm text-bodydark dark:text-bodydark1">
                    {template.description}
                  </p>
                  <div className="mt-3 text-xs font-mono text-primary">
                    IF {template.metric.toUpperCase()} {template.operator === 'lt' ? '<' : '>'} {template.threshold} FOR {template.duration_value} {template.duration_unit}
                  </div>
                </button>
              ))}
            </div>

            <div className="text-center pt-4 border-t border-stroke dark:border-strokedark">
              <button
                onClick={() => setStep('form')}
                className="text-primary hover:underline font-medium"
              >
                Or build your own custom rule →
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit}>
            {/* Campaign Selection (if not pre-selected) */}
            {!initialCampaignId && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  Campaign *
                </label>
                {loadingCampaigns ? (
                  <div className="w-full rounded border border-stroke bg-transparent py-3 px-5 text-bodydark dark:text-bodydark1">
                    Loading campaigns...
                  </div>
                ) : (
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    required
                    className="w-full rounded border border-stroke bg-transparent py-3 px-5 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                  >
                    <option value="">Select campaign...</option>
                    {campaigns.length > 0 ? (
                      campaigns.map((campaign) => (
                        <option key={campaign.unified_id} value={campaign.unified_id}>
                          {campaign.campaign_name} ({campaign.platform})
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No active campaigns available</option>
                    )}
                  </select>
                )}
              </div>
            )}

            {/* Rule Condition */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-black dark:text-white mb-2">
                Rule Condition
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-bodydark dark:text-bodydark1">IF</span>
                
                <select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  className="rounded border border-stroke bg-transparent py-2 px-3 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                >
                  <option value="roas">ROAS</option>
                  <option value="ctr">CTR</option>
                  <option value="cpa">CPA</option>
                  <option value="spend">Daily Spend</option>
                  <option value="cvr">Conversion Rate</option>
                  <option value="health_score">Health Score</option>
                  <option value="conversions">Conversions</option>
                  <option value="frequency">Frequency</option>
                </select>

                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  className="rounded border border-stroke bg-transparent py-2 px-3 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                >
                  <option value="lt">&lt;</option>
                  <option value="gt">&gt;</option>
                  <option value="lte">≤</option>
                  <option value="gte">≥</option>
                  <option value="eq">=</option>
                </select>

                <input
                  type="number"
                  step="0.01"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  placeholder="3.0"
                  required
                  className="w-24 rounded border border-stroke bg-transparent py-2 px-3 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                />

                <span className="text-sm text-bodydark dark:text-bodydark1">FOR</span>

                <input
                  type="number"
                  min="1"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  placeholder="3"
                  required
                  className="w-20 rounded border border-stroke bg-transparent py-2 px-3 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                />

                <select
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value)}
                  className="rounded border border-stroke bg-transparent py-2 px-3 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
                >
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
              </div>
            </div>

            {/* Preview */}
            {getPreviewText() && (
              <div className="mb-4 p-4 bg-primary bg-opacity-10 rounded-lg">
                <div className="text-sm font-medium text-bodydark dark:text-bodydark1 mb-1">
                  Preview:
                </div>
                <div className="text-primary font-mono text-sm">
                  {getPreviewText()}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900 dark:bg-opacity-20 text-red-600 dark:text-red-400 rounded">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-stroke dark:border-strokedark">
              {step === 'form' && !editRule && (
                <button
                  type="button"
                  onClick={() => setStep('templates')}
                  className="text-bodydark dark:text-bodydark1 hover:text-black dark:hover:text-white"
                >
                  ← Back to templates
                </button>
              )}
              <div className="flex items-center gap-3 ml-auto">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 border border-stroke dark:border-strokedark rounded hover:bg-gray-50 dark:hover:bg-meta-4 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50"
                >
                  {loading ? 'Creating...' : editRule ? 'Update Rule' : 'Create Rule →'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
